import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import { getAllTenderTasks, updateTenderTask } from '../../services/tenderService';
import toast from 'react-hot-toast';

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-muted text-muted-foreground' },
  medium: { label: 'Medium', color: 'bg-blue-500/15 text-blue-400' },
  high: { label: 'High', color: 'bg-warning/15 text-warning' },
  critical: { label: 'Critical', color: 'bg-error/15 text-error' },
};

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-muted text-muted-foreground', icon: 'Clock' },
  in_progress: { label: 'In Progress', color: 'bg-blue-500/15 text-blue-400', icon: 'PlayCircle' },
  completed: { label: 'Completed', color: 'bg-success/15 text-success', icon: 'CheckCircle2' },
  overdue: { label: 'Overdue', color: 'bg-error/15 text-error', icon: 'AlertCircle' },
};

const DEPARTMENTS = ['All', 'Finance', 'HR', 'Engineering', 'Procurement', 'Legal', 'IT', 'Operations'];

const GeneratedTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [editingTask, setEditingTask] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const data = await getAllTenderTasks();
      setTasks(data);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await updateTenderTask(taskId, { taskStatus: newStatus });
      setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, taskStatus: newStatus } : t));
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleProgressChange = async (taskId, progress) => {
    try {
      await updateTenderTask(taskId, { progress: parseInt(progress) });
      setTasks((prev) => prev.map((t) => t.id === taskId ? { ...t, progress: parseInt(progress) } : t));
    } catch {
      toast.error('Failed to update progress');
    }
  };

  const openEdit = (task) => {
    setEditingTask(task.id);
    setEditForm({ assignee: task.assignee || '', dueDate: task.dueDate || '', priority: task.priority || 'medium' });
  };

  const saveEdit = async () => {
    try {
      await updateTenderTask(editingTask, editForm);
      setTasks((prev) => prev.map((t) => t.id === editingTask ? { ...t, ...editForm } : t));
      setEditingTask(null);
      toast.success('Task updated');
    } catch {
      toast.error('Failed to save task');
    }
  };

  const filtered = tasks.filter((t) => {
    const q = search.toLowerCase();
    const matchSearch = !q || t?.title?.toLowerCase().includes(q) || t?.department?.toLowerCase().includes(q) || t?.tenderName?.toLowerCase().includes(q);
    const matchDept = deptFilter === 'All' || t?.department === deptFilter;
    const matchStatus = !statusFilter || t?.taskStatus === statusFilter;
    const matchPriority = !priorityFilter || t?.priority === priorityFilter;
    return matchSearch && matchDept && matchStatus && matchPriority;
  });

  const byDept = filtered.reduce((acc, t) => {
    if (!acc[t.department]) acc[t.department] = [];
    acc[t.department].push(t);
    return acc;
  }, {});

  const stats = {
    total: tasks.length,
    pending: tasks.filter((t) => t.taskStatus === 'pending').length,
    inProgress: tasks.filter((t) => t.taskStatus === 'in_progress').length,
    completed: tasks.filter((t) => t.taskStatus === 'completed').length,
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <MainLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Icon name="ListTodo" size={24} className="text-primary" />
              Generated Tasks
            </h1>
            <p className="text-muted-foreground text-sm mt-0.5">Auto-generated tasks from tender analysis</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total Tasks', value: stats.total, icon: 'ListTodo', color: 'text-primary' },
            { label: 'Pending', value: stats.pending, icon: 'Clock', color: 'text-muted-foreground' },
            { label: 'In Progress', value: stats.inProgress, icon: 'PlayCircle', color: 'text-blue-400' },
            { label: 'Completed', value: stats.completed, icon: 'CheckCircle2', color: 'text-success' },
          ].map((s) => (
            <div key={s.label} className="bg-card border border-border rounded-xl p-4">
              <Icon name={s.icon} size={18} className={`${s.color} mb-2`} />
              <div className="text-2xl font-bold text-foreground">{s.value}</div>
              <div className="text-xs text-muted-foreground">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 flex flex-wrap gap-3">
          <div className="flex-1 min-w-48 relative">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tasks..."
              className="w-full bg-muted/40 border border-border rounded-lg pl-9 pr-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}
            className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">All Statuses</option>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
            className="bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
            <option value="">All Priorities</option>
            {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* Tasks by Department */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="Loader2" size={28} className="animate-spin text-primary" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-card border border-border rounded-2xl">
            <Icon name="ListTodo" size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No tasks found</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(byDept).map(([dept, deptTasks]) => (
              <div key={dept} className="bg-card border border-border rounded-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 bg-muted/20 border-b border-border">
                  <Icon name="Building2" size={16} className="text-primary" />
                  <span className="font-semibold text-foreground">{dept}</span>
                  <span className="text-xs text-muted-foreground ml-1">({deptTasks.length})</span>
                </div>
                <div className="divide-y divide-border/50">
                  {deptTasks.map((task) => {
                    const sc = STATUS_CONFIG[task?.taskStatus] || STATUS_CONFIG.pending;
                    const pc = PRIORITY_CONFIG[task?.priority] || PRIORITY_CONFIG.medium;
                    const isEditing = editingTask === task.id;
                    return (
                      <div key={task.id} className="px-5 py-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap mb-1">
                              <span className="font-medium text-sm text-foreground">{task.title}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pc.color}`}>{pc.label}</span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${sc.color}`}>
                                <Icon name={sc.icon} size={10} />
                                {sc.label}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-2">{task.description}</p>
                            {task.tenderName && (
                              <p className="text-xs text-primary">
                                <Icon name="FileText" size={11} className="inline mr-1" />
                                {task.tenderName}
                              </p>
                            )}
                            {isEditing ? (
                              <div className="mt-3 grid grid-cols-3 gap-2">
                                <input value={editForm.assignee} onChange={(e) => setEditForm((p) => ({ ...p, assignee: e.target.value }))}
                                  placeholder="Assignee" className="bg-muted/40 border border-border rounded-lg px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                                <input type="date" value={editForm.dueDate} onChange={(e) => setEditForm((p) => ({ ...p, dueDate: e.target.value }))}
                                  className="bg-muted/40 border border-border rounded-lg px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
                                <select value={editForm.priority} onChange={(e) => setEditForm((p) => ({ ...p, priority: e.target.value }))}
                                  className="bg-muted/40 border border-border rounded-lg px-2 py-1.5 text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                </select>
                                <button onClick={saveEdit} className="col-span-2 text-xs bg-primary text-primary-foreground rounded-lg py-1.5 hover:bg-primary/90">Save</button>
                                <button onClick={() => setEditingTask(null)} className="text-xs bg-muted text-muted-foreground rounded-lg py-1.5 hover:bg-muted/80">Cancel</button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                {task.assignee && <span><Icon name="User" size={11} className="inline mr-1" />{task.assignee}</span>}
                                {task.dueDate && <span><Icon name="Calendar" size={11} className="inline mr-1" />{formatDate(task.dueDate)}</span>}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <select value={task.taskStatus} onChange={(e) => handleStatusChange(task.id, e.target.value)}
                              className="text-xs bg-muted/40 border border-border rounded-lg px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary">
                              {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                            </select>
                            {!isEditing && (
                              <button onClick={() => openEdit(task)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground">
                                <Icon name="Edit2" size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Progress Bar */}
                        <div className="mt-3 flex items-center gap-3">
                          <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                            <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${task.progress || 0}%` }} />
                          </div>
                          <input type="range" min="0" max="100" value={task.progress || 0}
                            onChange={(e) => handleProgressChange(task.id, e.target.value)}
                            className="w-20 accent-primary cursor-pointer" />
                          <span className="text-xs text-muted-foreground w-8">{task.progress || 0}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default GeneratedTasks;
